/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useMemo, useState, useRef, useLayoutEffect
} from 'react';
import styled from 'styled-components/macro';
import {
  LoadingSpinner, Button, Spacer, Title, Text
} from 'components/atoms';
import { Modal, ModalConfirm, TabsLineList } from 'components/molecules'
import { toast } from 'react-toastify';
import { SubwebSideNavEditor, SubwebsPagesTableSelect, Toast } from 'containers';
import { useUserContext } from 'context/UserContext';
import { withRouter, matchPath } from 'react-router-dom';
import { locationType } from 'types';
import { useToggle } from 'hooks';
import { useSelector, useDispatch } from 'react-redux'
import { generateItem, addItemsToTree } from 'modules/SideNav/parser/navToTree'
import treeToNav from 'modules/SideNav/parser/treeToNav'
import SideNavEditorLinkForm from 'modules/SideNav/SideNavEditorLinkForm'
import { updateTree, updateSidenav } from 'redux/sidenavSlice'
import { updateSubwebSidenav } from 'services/requests'
import { generateKey } from 'services/generators'
import PropTypes from 'prop-types'

const MenuEditor = ({
  location: { pathname }, triggerAdd, triggerPublish
}) => {
  const [{ user }] = useUserContext();
  const [isOpen, toggle] = useToggle();
  const dispatch = useDispatch()
  const { tree } = useSelector(
    (state) => state.sidenav
  )

  const [confirmationSave, setConfirmationSave] = useState(
    {
      isOpen: false,
      hasConfirmed: false,
      nextSidenav: null,
    }
  )

  const [modalAdd, setModalAdd] = useState({
    isAddingPage: true,
    isAddingLink: false,
  })
  const [link, setLink] = useState({
    url: '',
    title: ''
  })

  const params = useMemo(() => {
    const match = matchPath(pathname, {
      path: '/:subweb/editor/:lang/nav',
      exact: false,
    });
    if (match && match.params) return match.params;
    return null;
  }, [pathname]);

  const subwebName = useMemo(() => {
    if (params && params.subweb) return params.subweb;
    return null;
  }, [params]);

  const subwebTitle = useMemo(() => {
    if (!subwebName || !user || !user.subwebs || user.subwebs < 0) return null
    const subwebActive = user.subwebs.find(
      subweb => subweb.slug === subwebName || encodeURIComponent(subweb.slug) === subwebName
    )
    return subwebActive ? subwebActive.description : null
  }, [subwebName, user])

  const computedSubwebLink = useMemo(() => {
    if (!subwebName) return null
    return `/${subwebName}`
  }, [subwebName])

  const lang = useMemo(() => {
    if (params && params.lang) return params.lang;
    return null;
  }, [params]);

  const isLoading = useMemo(() => !subwebName || !subwebTitle || !computedSubwebLink,
    [subwebTitle, subwebName, computedSubwebLink]
  )

  const handleAddPage = (page) => {
    const { id, slug, title } = page
    // add page to tree
    const treeWithPage = addItemsToTree(
      tree,
      {
        ...generateItem({ id, slug, title })
      }
    )
    dispatch(updateTree(treeWithPage))
    // dispatch tree with new page
    toggle()
  }

  const discardLink = () => {
    setLink({ url: '', title: '' })
    toggle()
  }

  const handleAddLink = () => {
    const treeWithPage = addItemsToTree(
      tree,
      {
        ...generateItem({ id: generateKey(link.title), slug: link.url, title: link.title })
      }
    )
    dispatch(updateTree(treeWithPage))
    discardLink()
  }

  const handleSave = () => {
    setConfirmationSave(
      {
        isOpen: true,
        hasConfirmed: false,
        nextSidenav: treeToNav(tree),
      }
    )
  }

  const handleConfirmationSave = async (hasConfirmed) => {
    const sidenav = confirmationSave.nextSidenav
    const subweb = subwebName

    if (hasConfirmed) {
      dispatch(updateSidenav({ sidenav: confirmationSave.nextSidenav, subweb: subwebName }))
      try {
        await updateSubwebSidenav({ sidenavData: sidenav, subweb, language: lang })
        toast.success(
          <Toast
            type="success"
            title="Menu de navegação publicado."
          />
        );
      } catch (err) {
        toast.error(
          <Toast
            type="error"
            title="Erro a publicar menu de navegação."
            description={err.message}
          />
        );
      }
      setConfirmationSave({
        isOpen: false,
        hasConfirmed: false,
        nextSidenav: null,
      })
    }
    setConfirmationSave({
      isOpen: false,
      hasConfirmed: false,
      nextSidenav: null,
    })
  }

  const tabsAddPage = useMemo(() => [
    {
      text: 'página',
      isActive: modalAdd.isAddingPage,
      onClick: () => setModalAdd({ isAddingPage: true, isAddingLink: false }),
    },
    {
      text: 'link',
      isActive: modalAdd.isAddingLink,
      onClick: () => setModalAdd({ isAddingPage: false, isAddingLink: true }),
    }
  ], [modalAdd])

  // Add slider, only run after render
  // https://stackoverflow.com/a/53254028
  const isFirstUpdatePublish = useRef(true);
  const isFirstUpdateAdd = useRef(true);

  useLayoutEffect(() => {
    if (isFirstUpdatePublish.current) {
      isFirstUpdatePublish.current = false;
      return;
    }
    handleSave()
  }, [triggerPublish]);

  // opens add modal
  useLayoutEffect(() => {
    if (isFirstUpdateAdd.current) {
      isFirstUpdateAdd.current = false;
      return;
    }
    toggle()
  }, [triggerAdd]);

  const renderPopups = () => (
    <>
      <ModalConfirm
        isOpen={confirmationSave.isOpen}
        toggle={() => setConfirmationSave({
          ...confirmationSave,
          isOpen: !confirmationSave.isOpen
        })}
        onChange={handleConfirmationSave}
        title="Estas alterações serão automaticamente publicadas."
        message="Pretende continuar?"
        textConfirm="Publicar"
        textDiscard="Cancelar"
      />
      <Modal isOpen={isOpen} toggle={toggle}>
        {modalAdd.isAddingPage && (
          <>
            <ModalHeader>
              <Title>Adicionar página</Title>
              <TabsLineList tabs={tabsAddPage} modifiers={['small']} />
            </ModalHeader>
            <Text>Selecione a página que quer adicionar.</Text>
            <Spacer top="xl" />
            <SubwebsPagesTableSelect subweb={subwebName} lang={lang} onAddPage={handleAddPage} />
            <Modal.Actions>
              <Button
                type="button"
                modifiers={['marginRight', 'capitalize']}
                onClick={toggle}
              >
            Cancelar
              </Button>
            </Modal.Actions>
          </>
        )}
        {modalAdd.isAddingLink && (
          <>
            <ModalHeader>
              <Title>Adicionar link</Title>
              <TabsLineList tabs={tabsAddPage} modifiers={['small']} />
            </ModalHeader>
            <Text>Preencha o formulário para adicionar um link.</Text>
            <Spacer top="xl" />
            <SideNavEditorLinkForm
              initialValues={link}
              onChange={setLink}
              onSubmit={handleAddLink}
              onDiscard={discardLink}
            />
          </>
        )}
      </Modal>
    </>
  )

  return (
    <>
      {renderPopups()}
      <Wrapper>
        {
          isLoading
            ? (
              <MenuContent>
                <LoadingSpinner />
              </MenuContent>
            )
            : (
              <SubwebSideNavEditor
                subweb={subwebName}
                lang={lang}
              />
            )
        }
      </Wrapper>
    </>
  );
};

const Wrapper = styled.div`
  min-height: 100vh;
  overflow-x: hidden;
  overflow-y: auto;
  position: relative;
  width: ${props => props.theme.components.editor.sideNavEditorWidth};
`;

const Actions = styled.div`
  display: flex;
  margin-bottom: ${props => props.theme.spacing.xs};
  justify-content: space-between;
`

Actions.Start = styled.div`
  display: flex;
`

const MenuContent = styled.div`
  padding: ${props => props.theme.spacing.s};
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
`

MenuEditor.propTypes = {
  location: locationType.isRequired,
  triggerAdd: PropTypes.bool,
  triggerPublish: PropTypes.bool,
};

MenuEditor.defaultProps = {
  triggerAdd: false,
  triggerPublish: false,
}

export default withRouter(MenuEditor);
